import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserAuth } from "../../lib/userAuth";
import { LogIn, LogOut } from "lucide-react";

const links = [
    { href: "/library", label: "Library" },
    { href: "/blog", label: "Blog" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#faq", label: "FAQ" },
];

export default function Nav() {
    const [open, setOpen] = React.useState(false);
    const location = useLocation();
    const { user, signInWithGoogle, logout } = useUserAuth() || {};
    const isAuthed = user && user !== false;

    return (
        <header
            className="sticky top-0 z-50 border-b border-hairline bg-page/85 backdrop-blur-md"
            data-testid="site-nav"
        >
            <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 lg:h-20 lg:px-10">
                <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
                    <span className="grid h-7 w-7 place-items-center bg-coral text-ink font-display text-base font-black">
                        s
                    </span>
                    <span className="font-display text-lg font-black tracking-tight text-ink">
                        simple<span className="text-coral">.</span>digital
                    </span>
                </Link>

                <nav className="hidden items-center gap-6 lg:flex">
                    {links.map((l) => (
                        <Link
                            key={l.href}
                            to={l.href}
                            className="text-sm font-medium uppercase tracking-[0.16em] text-ink/65 transition-colors hover:text-coral"
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    {isAuthed ? (
                        <div
                            className="hidden items-center gap-3 lg:flex"
                            data-testid="nav-user-menu"
                        >
                            {user?.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="h-8 w-8 rounded-full border border-ink/25 object-cover"
                                />
                            ) : (
                                <span className="grid h-8 w-8 place-items-center rounded-full border border-ink/25 bg-white/10 font-display text-xs font-black uppercase text-ink">
                                    {(user?.name || user?.email || "?").slice(0, 1)}
                                </span>
                            )}
                            <span
                                className="text-xs uppercase tracking-[0.16em] text-ink/70"
                                data-testid="nav-user-name"
                            >
                                {(user?.name || user?.email || "").split(" ")[0]}
                            </span>
                            <button
                                onClick={() => logout && logout()}
                                className="grid h-9 w-9 place-items-center border-2 border-ink/25 text-ink/70 transition-colors hover:border-coral hover:text-coral"
                                data-testid="nav-logout"
                                aria-label="Sign out"
                                title="Sign out"
                            >
                                <LogOut className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => signInWithGoogle && signInWithGoogle(location.pathname)}
                            className="hidden items-center gap-2 border-2 border-ink/25 px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:border-coral hover:text-coral lg:inline-flex"
                            data-testid="nav-signin"
                        >
                            <LogIn className="h-3.5 w-3.5" />
                            Sign in
                        </button>
                    )}
                    <a
                        href={location.pathname === "/" ? "#contact" : "/#contact"}
                        className="hidden border-2 border-coral bg-coral px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:bg-coral-hover lg:inline-block"
                        data-testid="nav-cta-buy"
                    >
                        Get started
                    </a>
                    <button
                        className="grid h-10 w-10 place-items-center border-2 border-ink/20 lg:hidden"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        <span className="block h-0.5 w-5 bg-white before:absolute before:h-0.5 before:w-5 before:-translate-y-1.5 before:bg-white after:absolute after:h-0.5 after:w-5 after:translate-y-1.5 after:bg-white"></span>
                    </button>
                </div>
            </div>

            {open && (
                <div className="border-t border-hairline bg-page lg:hidden" data-testid="nav-mobile-menu">
                    <div className="flex flex-col px-5 py-4">
                        {links.map((l) => (
                            <Link
                                key={l.href}
                                to={l.href}
                                onClick={() => setOpen(false)}
                                className="border-b border-hairline py-3 font-display text-sm font-bold uppercase tracking-wider text-ink/85"
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <a
                            href="/#contact"
                            onClick={() => setOpen(false)}
                            className="mt-4 border-2 border-coral bg-coral px-5 py-3 text-center font-display font-bold uppercase tracking-wider text-ink"
                            data-testid="nav-mobile-cta-buy"
                        >
                            Get started
                        </a>
                        {isAuthed ? (
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    logout && logout();
                                }}
                                className="mt-3 border-2 border-ink/25 px-5 py-3 text-center font-display text-sm font-bold uppercase tracking-wider text-ink"
                                data-testid="nav-mobile-logout"
                            >
                                Sign out
                            </button>
                        ) : (
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    signInWithGoogle && signInWithGoogle(location.pathname);
                                }}
                                className="mt-3 border-2 border-ink/25 px-5 py-3 text-center font-display text-sm font-bold uppercase tracking-wider text-ink"
                                data-testid="nav-mobile-signin"
                            >
                                Sign in with Google
                            </button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
